import { Component } from "@angular/core";
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
} from "@angular/forms";
import {
  LoadingController,
  ModalController,
  NavController,
} from "@ionic/angular";
import { loadingProps } from "src/utils/util.functions";
import { AuthService } from "src/app/services/auth.service";
import { ResponseAPI } from "src/models/user.model";
import { UtilService } from "src/app/services/util.service";
import { rgxPassword } from "src/utils/regex";
import { Router } from "@angular/router";
import { CrudService } from "src/app/services/crud.service";
import { Platform } from "@ionic/angular";
import { StatusBar } from "@capacitor/status-bar";
import { SplashScreen } from "@capacitor/splash-screen";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";

@Component({
    selector: "app-login",
    templateUrl: "login.page.html",
    styleUrls: ["login.page.scss"],
    standalone: false
})
export class LoginPage {
  loginForm: UntypedFormGroup;
  visiblePassword = false;
  isCordova = false;
  packageName: string = "";
  isIos = false;
  constructor(
    private fb: UntypedFormBuilder,
    public loadingController: LoadingController,
    public modalController: ModalController,
    public navCtrl: NavController,
    public authService: AuthService,
    public route: Router,
    public utilService: UtilService,
    private platform: Platform,
  ) {
    this.loginForm = fb.group({
      email: ["", [Validators.required, Validators.email]],
      password: [
        "",
        [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(15),
          Validators.pattern(rgxPassword),
        ],
      ],
      rememberMe: [true],
    });
  }

  getPackageName() {}

  async ionViewWillEnter() {
    setTimeout(
      async () => {
        this.isCordova = Capacitor.isNativePlatform();
        this.isIos = this.platform.is("ios");
        if (this.isCordova) {
          try {
            const info = await App.getInfo();
            this.packageName = info.id;
          } catch (e) {
            this.packageName = "";
          }
        }

        await this.showLoading();

        try {
          const user = await this.authService.dataAuth();
          if (user?.token) {
            const redirectRoute = await this.authService.setRedirectRole();
            await this.loadingController.dismiss();
            if (redirectRoute.length) {
              await this.route.navigate(redirectRoute);
            }
          } else {
            await this.loadingController.dismiss();
          }
        } finally {
          if (Capacitor.isNativePlatform()) {
            await StatusBar.hide();
            await SplashScreen.hide();
          }
        }
      },
      Capacitor.isNativePlatform() && this.platform.is("ios") ? 3000 : 0,
    );
  }

  async showLoading() {
    loadingProps.mode = this.platform.is("ios") ? "ios" : "md";
    const loading = await this.loadingController.create(loadingProps);
    return await loading.present();
  }

  async tryLogin() {
    if (this.loginForm.invalid) {
      if (Capacitor.isNativePlatform()) {
        await StatusBar.hide();
        await SplashScreen.hide();
      }
      await this.loadingController.dismiss();
      return false;
    }

    this.authService.loginUser(this.loginForm.value).subscribe(
      async (response: ResponseAPI) => {
        if (response.success) {
          await this.setLoginApp(response);
        }
      },
      async (error) => {
        await this.utilService.showToast("Login failure");
        await this.loadingController.dismiss();
      },
    );
    await this.showLoading();
  }

  private async setLoginApp(response: ResponseAPI) {
    if (!this.isCordova) {
      await this.authService.saveAuth(response.data);
      await this.loadingController.dismiss();
      await this.route.navigate(await this.authService.setRedirectRole());
      await this.utilService.showToastLogin("Welcome to GoSpread!");
    } else {
      switch (this.packageName) {
        case "gospread.tech.app":
          if (response.data.userType === 0) {
            await this.authService.saveAuth(response.data);
            await this.loadingController.dismiss();
            await this.route.navigate(await this.authService.setRedirectRole());
            await this.utilService.showToastLogin("Welcome to GoSpread!");
          } else {
            await this.utilService.showToast("Login failure");
            await this.loadingController.dismiss();
          }
          break;
        case "gospread.partners.tech.app":
          if (response.data.userType === 1 || response.data.userType === 4) {
            await this.authService.saveAuth(response.data);
            await this.loadingController.dismiss();
            await this.route.navigate(await this.authService.setRedirectRole());
            await this.utilService.showToastLogin("Welcome to GoSpread!");
          } else {
            await this.utilService.showToast("Login failure");
            await this.loadingController.dismiss();
          }
          break;
        default:
          await this.utilService.showToast("Login failure");
          await this.loadingController.dismiss();
          break;
      }
    }
  }

  async openModal(): Promise<any> {
    const modal = await this.modalController.create({
      component: ForgotenPassPage,
      cssClass: "modal-overlay",
    });
    return await modal.present();
  }
}

@Component({
    selector: "app-forgoten-pass",
    templateUrl: "forgoten-pass/forgoten-pass.html",
    styleUrls: ["login.page.scss"],
    standalone: false
})
export class ForgotenPassPage {
  forgotenForm: UntypedFormGroup;

  constructor(
    private modalCtrl: ModalController,
    private fb: UntypedFormBuilder,
    private crud: CrudService,
    private util: UtilService,
  ) {
    this.forgotenForm = fb.group({
      email: ["", [Validators.required, Validators.email]],
    });
  }

  async close() {
    await this.modalCtrl.dismiss();
  }

  sendForm() {
    if (this.forgotenForm.invalid) {
      return false;
    }

    this.crud
      .postData("account/esqueci-senha", null, this.forgotenForm.value)
      .subscribe(
        async (response: ResponseAPI) => {
          if (response.success) {
            await this.util.showToast(
              "A new password has been sent to your email.",
            );
            await this.close();
          }
        },
        async (error) => {
          await this.util.showToast(
            "An error occurred while submitting your request.",
          );
          await this.close();
        },
      );
  }
}
